import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { Loader2, ShieldCheck } from 'lucide-react';
import JourneyLayout from '../../components/JourneyLayout';

function VerifyDigilockerEkyc() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      const profileId = localStorage.getItem('profile_id') || '';
      
      if (!token || !profileId) {
        navigate('/apply-now');
        return;
      }
      setCheckingAuth(false);
    };

    checkLoginStatus();
  }, [navigate]);

  useEffect(() => {
    if (checkingAuth) return;

    const checkEkycStatus = async () => {
      const profileId = localStorage.getItem('profile_id') || '';
      if (!profileId) {
        setLoading(false);
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/apply-now'), 2000);
        return;
      }

      try {
        // First, get dashboard data to fetch lead_id
        const dashboardResponse = await authAPI.getDashboardData(profileId);
        
        if (dashboardResponse.Status !== 1) {
          setError('Failed to fetch dashboard data. Please try again.');
          setLoading(false);
          return;
        }

        const leadId = dashboardResponse.Data?.profile_details?.lead_id || '';
        
        // Now call check eKYC status API
        const response = await authAPI.checkEkycStatus({
          profile_id: profileId,
          lead_id: leadId,
        });

        if (response.Status === 1) {
          // Set loading to false before navigation to prevent blank page
          setLoading(false);
          // Navigate to congratulations page
          navigate('/congratulations');
        } else if (response.Status === 2) {
          // If Status is 2, redirect to e-kyc route
          setLoading(false);
          navigate('/e-kyc');
        } else {
          setError(response.Message || 'Failed to verify eKYC status. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to verify eKYC status. Please try again.');
        setLoading(false);
      }
    };

    checkEkycStatus();
  }, [navigate, checkingAuth]);

  if (checkingAuth) {
    return (
      <JourneyLayout journeyStep={7}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme" />
        </div>
      </JourneyLayout>
    );
  }

  if (loading) {
    return (
      <JourneyLayout journeyStep={7}>
        <div className="flex flex-col items-center text-center space-y-6 py-12">
          <div className="w-16 h-16 rounded-full bg-theme/10 border-2 border-theme/20 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-theme" />
          </div>
          <Loader2 className="h-10 w-10 text-theme animate-spin" />
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Verifying Digilocker eKYC</h2>
          <p className="text-gray-600 max-w-sm text-lg">
            Hang tight while we fetch the latest status. This usually takes a moment.
          </p>
        </div>
      </JourneyLayout>
    );
  }

  if (error) {
    return (
      <JourneyLayout journeyStep={7}>
        <div className="flex flex-col items-center text-center space-y-6 py-12">
          <div className="w-16 h-16 rounded-full bg-theme/10 border-2 border-theme/20 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-theme" />
          </div>
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 max-w-md w-full">
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
          <button
            onClick={() => navigate('/apply-now')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-theme to-theme2 text-white px-8 py-3 rounded-2xl font-semibold hover:opacity-90 transition-opacity shadow-lg"
          >
            Back to Start
          </button>
        </div>
      </JourneyLayout>
    );
  }

  return null;
}

export default VerifyDigilockerEkyc;

